import { useEffect } from "@wordpress/element";
import { useBlockProps, InnerBlocks } from "@wordpress/block-editor";
import { createBlock } from "@wordpress/blocks";
import { useSelect, useDispatch } from "@wordpress/data";
import LayoutSelector from "../LayoutSelector";
import ContainerInspectorControls from "./InspectorControls";
import classNames from "classnames";

export default function Edit({ attributes, setAttributes, clientId }) {
  const {
    columns,
    rows,
    columnsWidth,
    isChild,
    width,
    height,
    layout,
    alignItems,
    justifyContent,
    direction,
    wrap,
    gridTemplateColumns,
    gridTemplateRows,
    parentLayout,
    columnSpan,  // Add columnSpan
    rowSpan,     // Add rowSpan
  } = attributes;

  const { replaceInnerBlocks, replaceBlock } = useDispatch("core/block-editor");
  const innerBlocks = useSelect(
    (select) => select("core/block-editor").getBlocks(clientId),
    [clientId]
  );
  const selectedBlockClientId = useSelect(
    (select) => select("core/block-editor").getSelectedBlockClientId(),
    []
  );
  const isParentSelected = selectedBlockClientId === clientId;

  const parentBlock = useSelect(
    (select) => {
      const parentClientId =
        select("core/block-editor").getBlockRootClientId(clientId);
      if (parentClientId) {
        const parent = select("core/block-editor").getBlock(parentClientId);
        return parent?.name === "brandy/container-block" ? parent : null;
      }
      return null;
    },
    [clientId]
  );

  // Synchronize parentLayout with the layout of the parent block
  useEffect(() => {
    if (isChild && parentBlock && parentBlock.attributes.layout !== parentLayout) {
      setAttributes({ parentLayout: parentBlock.attributes.layout });
    }
  }, [isChild, parentBlock, parentLayout, setAttributes]);

  const currentDeviceType = useSelect(
    (select) => select("core/editor").getDeviceType() || "Desktop",
    []
  );
  const deviceKey = currentDeviceType.toLowerCase();

  const shouldApplyMaxWidth =
    isChild && parentBlock && parentBlock.attributes.layout === "flex";

  const currentColumns = columns?.[deviceKey] ?? columns?.desktop ?? 1;
  const currentRows = rows?.[deviceKey] ?? rows?.desktop ?? 1;
  const currentWidth = width?.[deviceKey] ?? 100;
  const currentHeight = height?.[deviceKey] ?? null;
  const currentAlignItems = alignItems?.[deviceKey] ?? "center";
  const currentJustifyContent = justifyContent?.[deviceKey] ?? "center";
  const currentDirection = direction?.[deviceKey] ?? "row";
  const currentWrap =
    wrap?.[deviceKey] ?? (deviceKey === "mobile" ? "wrap" : "nowrap");
  const currentColumnSpan = columnSpan?.[deviceKey] ?? columnSpan?.desktop ?? 1;  // Add currentColumnSpan
  const currentRowSpan = rowSpan?.[deviceKey] ?? rowSpan?.desktop ?? 1;         // Add currentRowSpan

  const blockProps = useBlockProps({
    className: classNames("brandy-parent-container-block", {
      "brandy-child-container-block": isChild,
      "brandy-container-has-children": innerBlocks.length > 0,
      "brandy-container-parent-show-layout-options":
        innerBlocks.length === 0 && !isChild,
      "brandy-grid-layout": layout === "grid",
      "brandy-flex-layout": layout === "flex",
      "brandy-container-multi-rows": currentRows > 1,
    }),
    style: {
      ...(shouldApplyMaxWidth
        ? { maxWidth: `${currentWidth}%`, width: `100%` }
        : { width: `100%` }),
      ...(currentHeight !== null ? { height: `${currentHeight}px` } : {}),
      ...(layout === "flex"
        ? {
            "--align-items": currentAlignItems,
            "--justify-content": currentJustifyContent,
            "--flex-direction": currentDirection,
            "--flex-wrap": currentWrap,
          }
        : {
            "--grid-template-columns": `${
              gridTemplateColumns?.[deviceKey]?.join(" ") || "1fr"
            }`,
            "--grid-template-rows": `${
              gridTemplateRows?.[deviceKey]?.join(" ") || "1fr"
            }`,
          }),
          ...(isChild && parentLayout === "grid"
            ? {
                "grid-column": `span ${currentColumnSpan}`,  // Apply column span
                "grid-row": `span ${currentRowSpan}`,        // Apply row span
              }
            : {}),
    },
  });

  useEffect(() => {
    const updatedColumnsWidth = innerBlocks
      .filter((block) => block.name === "brandy/container-block")
      .map((block) => block.attributes.width[deviceKey] ?? 100)
      .filter((width) => typeof width === "number");

    if (
      updatedColumnsWidth.length > 0 &&
      JSON.stringify(updatedColumnsWidth) !== JSON.stringify(columnsWidth)
    ) {
      setAttributes({ columnsWidth: updatedColumnsWidth });
    }
  }, [innerBlocks.length, columnsWidth, setAttributes, deviceKey]);

  useEffect(() => {
    const validChildren = innerBlocks.filter(
      (block) => block.name === "brandy/container-block"
    );
    const newColumns = validChildren.length;
    const newRows = Math.ceil(newColumns / (currentColumns || 1));

    if (newRows !== currentRows) {
      setAttributes({
        rows: { ...rows, [deviceKey]: newRows },
      });
    }
  }, [innerBlocks.length, currentColumns, currentRows, setAttributes, deviceKey]);

  const handleLayoutChange = (layoutSelected) => {
    if (
      currentColumns === layoutSelected.columns &&
      JSON.stringify(columnsWidth) === JSON.stringify(layoutSelected.template)
    ) {
      return;
    }

    const newColumns = layoutSelected.columns;
    const newRows = Math.ceil(layoutSelected.template.length / newColumns);

    if (newColumns === 1 && newRows === 1) {
      const childBlock = createBlock("brandy/container-block", {
        isChild: true,
        className: "brandy-block-special",
        width: { desktop: 100, tablet: 100, mobile: 100 },
        direction: { desktop: "column", tablet: "column", mobile: "column" },
        parentLayout: layout,
      });
      replaceBlock(clientId, childBlock);
      return;
    }

    const totalWidthPerRow = Array.from({ length: newRows }, (_, rowIndex) =>
      layoutSelected.template
        .slice(rowIndex * newColumns, (rowIndex + 1) * newColumns)
        .reduce((a, b) => a + b, 0)
    );

    const widthPercentages = layoutSelected.template.map((colWidth, index) => {
      const rowIndex = Math.floor(index / newColumns);
      return newRows > 1
        ? (colWidth / totalWidthPerRow[rowIndex]) * 100 - 2
        : (colWidth / totalWidthPerRow[rowIndex]) * 100;
    });

    const childBlocks = layoutSelected.template.map((width, index) => {
      const calculatedWidth = {
        desktop: widthPercentages[index],
        tablet: widthPercentages[index],
        mobile: layout === "flex" ? 100 : widthPercentages[index],
      };

      return createBlock("brandy/container-block", {
        isChild: true,
        width: calculatedWidth,
        direction: { desktop: "column", tablet: "column", mobile: "column" },
        parentLayout: layout,
      });
    });

    setAttributes({
      columns: { ...columns, [deviceKey]: newColumns },
      rows: { ...rows, [deviceKey]: newRows },
      columnsWidth: layoutSelected.template,
      direction: { desktop: "row", tablet: "row", mobile: "row" },
      wrap:
        !isChild && newRows > 1
          ? { desktop: "wrap", tablet: "wrap", mobile: "wrap" }
          : { desktop: "nowrap", tablet: "nowrap", mobile: "wrap" },
      gridTemplateColumns: {
        ...gridTemplateColumns,
        [deviceKey]: Array(newColumns).fill("1fr"),
      },
      gridTemplateRows: {
        ...gridTemplateRows,
        [deviceKey]: Array(newRows).fill("1fr"),
      },
    });

    replaceInnerBlocks(clientId, childBlocks, false);
  };

  const shouldShowLayoutSelector = () => {
    if (innerBlocks.length === 0 && !isChild) {
      if (parentBlock && parentBlock.attributes.layout === "grid") {
        const childBlock = createBlock("brandy/container-block", {
          isChild: true,
          width: { desktop: 100, tablet: 100, mobile: 100 },
          direction: { desktop: "column", tablet: "column", mobile: "column" },
          parentLayout: parentBlock.attributes.layout,
        });
        replaceBlock(clientId, childBlock);
        return false;
      }
      return true;
    }
    return false;
  };

  const blockId = `block-${clientId}`;

  const customStyles = `
    .editor-styles-wrapper #${blockId}.wp-block-brandy-container-block.brandy-grid-layout > .brandy-container-wrapper {
      justify-content: stretch;
    }
    .editor-styles-wrapper #${blockId}.wp-block-brandy-container-block > .brandy-container-wrapper > .block-editor-inner-blocks > .block-editor-block-list__layout,
    .editor-styles-wrapper #${blockId}.wp-block-brandy-container-block > .block-editor-inner-blocks > .block-editor-block-list__layout {
      ${
        layout === "grid"
          ? `
        display: grid;
        grid-template-columns: ${
          gridTemplateColumns?.[deviceKey]?.join(" ") || "1fr"
        };
        grid-template-rows: ${
          gridTemplateRows?.[deviceKey]?.join(" ") || "1fr"
        };
      `
          : `
        display: flex;
        align-items: ${currentAlignItems};
        justify-content: ${currentJustifyContent};
        flex-direction: ${currentDirection};
        flex-wrap: ${currentWrap};
      `
      }
      row-gap: 20px;
      column-gap: 20px;
    }
    .editor-styles-wrapper #${blockId}.wp-block-brandy-container-block {
      padding: 10px;
      box-shadow: 0px 0px #00000070;
      border-color: inherit;
      overflow: visible;
      order: initial;
    }
    .editor-styles-wrapper .block-editor-block-list__block.wp-block-brandy-container-block#${blockId} > .brandy-container-wrapper {
      width: 100%;
      margin-left: auto;
      margin-right: auto;
    }
  `;

  return (
    <div {...blockProps}>
      <style>{customStyles}</style>
      {shouldShowLayoutSelector() ? (
        <LayoutSelector onLayoutChange={handleLayoutChange} />
      ) : (
        <>
          {!isChild ? (
            <div
              className={`brandy-container-wrapper ${
                currentRows > 1 ? "brandy-container-multi-rows" : ""
              }`}
            >
              <InnerBlocks
                templateLock={false}
                renderAppender={() =>
                  isChild || isParentSelected ? (
                    <div className="brandy-default-appender-button brandy-root-appender-button">
                      <InnerBlocks.ButtonBlockAppender />
                    </div>
                  ) : null
                }
              />
            </div>
          ) : (
            <InnerBlocks
              templateLock={false}
              renderAppender={() =>
                isChild ? (
                  innerBlocks.length === 0 ? (
                    <InnerBlocks.ButtonBlockAppender />
                  ) : (
                    selectedBlockClientId === clientId && (
                      <div className="brandy-default-appender-button">
                        <InnerBlocks.ButtonBlockAppender />
                      </div>
                    )
                  )
                ) : (
                  isParentSelected && <InnerBlocks.ButtonBlockAppender />
                )
              }
            />
          )}
          {!shouldShowLayoutSelector() && (
            <ContainerInspectorControls
              attributes={attributes}
              setAttributes={setAttributes}
            />
          )}
        </>
      )}
    </div>
  );
}